import { useMemo } from "react"
import { useIntl } from "react-intl"

import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { useAppSelector } from "lib/hooks"
import { categorySelectors } from "reducers/categories"
import { QueueConversation } from "types"

import { BooleanCell, DateTimePillCell, NameCell } from "components/v2/conversationList/Cells"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

interface PostponedConversationListProps {
  conversations: QueueConversation[]
}

const PostponedConversationList = ({ conversations }: PostponedConversationListProps) => {
  const intl = useIntl()
  const categories = useAppSelector(categorySelectors.selectEntities)

  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "category",
      name: intl.formatMessage({ id: "label.overview.reason_for_visit" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.postponed_to" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const list = useMemo(
    () =>
      conversations.map(conv => {
        const isChildConversation = conv.profile?.type === "child"
        return {
          conversation: conv,
          values: {
            pnr: isChildConversation
              ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
              : formatPnr(conv.account?.pnr, conv.metadata?.region),
            name: (
              <NameCell
                name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
                isChild={isChildConversation}
              />
            ),
            category: categories[conv.categoryId].name,
            start: <DateTimePillCell value={conv.queuePriority} />,
            listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
            recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
            medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
          },
          sortValues: {
            ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent"], {
              categories
            }),
            category: sortValueFunctions.category(conv, { categories }),
            start: sortValueFunctions.queuePriority(conv)
          }
        } as ConversationListItem<QueueConversation>
      }),
    [categories, conversations]
  )

  return <ConversationTable title="Postponed" columns={columns} items={list} />
}

export default PostponedConversationList
